import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Dropdown,
  Modal,
  Form
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { toast } from "react-toastify";
import IconMenu from "../images/icon-menu.png";
import Logo from "../images/logo.png";
import { BsThreeDotsVertical } from "react-icons/bs";
import AppConstants from "../AppConstants";
import useAuthToken from "../hooks/useAuthToken";

interface HeaderProps {
  title: string;
  showMenu?: boolean;
  currentPage?:
    | "subscription"
    | "payment-methods"
    | "upgrade-plan"
    | "plan-details";
  // Subscription-related props (optional - only needed for subscription pages)
  subscriptionData?: {
    autoRenew: boolean;
    planName: string;
  };
  onAutoRenewalToggle?: () => void;
  onAutoRenewalStatusUpdate?: (autoRenew: boolean) => void; // New callback for API status update
  onDowngradePlan?: () => void;
  onCancelSubscription?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  title,
  showMenu = true,
  currentPage = "plan-details",
  subscriptionData,
  onAutoRenewalToggle,
  onAutoRenewalStatusUpdate,
  onDowngradePlan,
  onCancelSubscription
}) => {
  const navigate = useNavigate();
  const { userToken, ready, getUserIdFromToken } = useAuthToken();
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [downgradeUsers, setDowngradeUsers] = useState("");
  const [downgradeValidationError, setDowngradeValidationError] = useState("");

  // Internal state for auto-renewal status from API
  const [internalAutoRenew, setInternalAutoRenew] = useState<boolean | null>(
    null
  );
  const [isLoadingAutoRenew, setIsLoadingAutoRenew] = useState(false);
  const [licenseData, setLicenseData] = useState<any>(null);

  // Function to fetch auto-renewal status from license/usage API
  const fetchAutoRenewalStatus = async () => {
    if (!ready || !userToken) return;

    setIsLoadingAutoRenew(true);
    try {
      const response = await axios.get(AppConstants.LICENSE_USAGE_API, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json"
        }
      });

      if (response.data.success && response.data.data) {
        const autoRenewStatus = response.data.data.autoRenew;
        setInternalAutoRenew(autoRenewStatus);

        // Store the full license data for other uses (like cancel modal)
        setLicenseData(response.data.data);

        // Notify parent component if callback is provided
        if (onAutoRenewalStatusUpdate) {
          onAutoRenewalStatusUpdate(autoRenewStatus);
        }
      }
    } catch (error) {
      console.warn("⚠️ Failed to fetch auto-renewal status:", error);
      // Fallback to subscriptionData prop if API fails
      if (subscriptionData?.autoRenew !== undefined) {
        setInternalAutoRenew(subscriptionData.autoRenew);
      }
    } finally {
      setIsLoadingAutoRenew(false);
    }
  };

  // Fetch auto-renewal status on component mount and when auth is ready
  useEffect(() => {
    if (
      ready &&
      userToken &&
      (currentPage === "subscription" || currentPage === "upgrade-plan")
    ) {
      fetchAutoRenewalStatus();
    }
  }, [ready, userToken, currentPage]);

  // Helper function to format date to MM/DD/YYYY
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
      });
    } catch {
      return "N/A";
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of your account",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, logout!"
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/");
      }
    });
  };

  const handlePaymentMethodsClick = () => {
    navigate("/payment-methods");
  };

  const handleSubscriptionClick = () => {
    navigate("/plan-details");
  };

  const handleThreeDotsClick = () => {
    setShowOptionsModal(true);
  };

  // Validation function for downgrade users
  const validateDowngradeUsers = (value: string) => {
    const maxUsers = licenseData?.maxUsers || 9;
    const maxAllowed = maxUsers - 1;

    if (!value) {
      setDowngradeValidationError("Number of users is required");
      return false;
    }

    const numValue = parseInt(value);
    if (isNaN(numValue)) {
      setDowngradeValidationError("Please enter a valid number");
      return false;
    }

    if (numValue < 1) {
      setDowngradeValidationError("Number of users must be at least 1");
      return false;
    }

    if (numValue > maxAllowed) {
      setDowngradeValidationError(
        `Number of users must be less than ${maxUsers} (maximum ${maxAllowed} allowed)`
      );
      return false;
    }

    setDowngradeValidationError("");
    return true;
  };

  // Handle downgrade users input (only numbers)
  const handleDowngradeUsersChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;

    // Only allow numbers
    if (value === "" || /^\d+$/.test(value)) {
      setDowngradeUsers(value);
      if (value) {
        validateDowngradeUsers(value);
      } else {
        setDowngradeValidationError("");
      }
    }
  };

  const handleAutoRenewalToggle = async () => {
    if (!ready || !userToken) {
      toast.error("Authentication required", {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      // Use internal state or fallback to prop
      const currentAutoRenewStatus =
        internalAutoRenew !== null
          ? internalAutoRenew
          : subscriptionData?.autoRenew || false;
      const newAutoRenewStatus = !currentAutoRenewStatus;

      const response = await axios.post(
        AppConstants.SUBSCRIPTION_TOGGLE_AUTO_RENEW_API,
        {
          autoRenew: newAutoRenewStatus
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        // Refresh auto-renewal status from API after successful toggle
        await fetchAutoRenewalStatus();

        // Call the parent's handler if provided for general toggle event
        if (onAutoRenewalToggle) {
          onAutoRenewalToggle();
        }

        // Show success message
        toast.success(
          response.data.message ||
            `Auto-renewal ${
              newAutoRenewStatus ? "enabled" : "disabled"
            } successfully!`,
          {
            position: "top-right",
            autoClose: 3000
          }
        );

        // Close the modal
        setShowOptionsModal(false);
      } else {
        toast.error(response.data.message || "Failed to toggle auto-renewal", {
          position: "top-right",
          autoClose: 3000
        });
      }
    } catch (error: any) {
      console.error("❌ Error toggling auto-renewal:", error);
      toast.error("Failed to toggle auto-renewal", {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleDowngradePlan = () => {
    setShowOptionsModal(false);
    setShowDowngradeModal(true);
  };

  const handleCancelSubscription = () => {
    setShowOptionsModal(false);
    setShowCancelModal(true);
  };

  const handleDowngradeSubmit = async () => {
    if (!validateDowngradeUsers(downgradeUsers)) {
      return;
    }

    if (!ready || !userToken) {
      toast.error("Authentication required", {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      // Prepare the payload
      const payload = {
        customerId: getUserIdFromToken(), // Get customerId from token
        newMaxUsers: parseInt(downgradeUsers)
      };

      const response = await axios.post(
        AppConstants.SUBSCRIPTION_DOWNGRADE_API,
        payload,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        // Close modal and reset form
        setShowDowngradeModal(false);
        setDowngradeUsers("");
        setDowngradeValidationError("");

        // Refresh license data to get updated information
        await fetchAutoRenewalStatus();

        // Show success message
        const message = response.data.message || "Plan downgraded successfully";
        toast.success(message, {
          position: "top-right",
          autoClose: 3000
        });

        // Call parent callback if provided
        if (onDowngradePlan) {
          onDowngradePlan();
        }
      } else {
        toast.error(response.data.message || "Failed to downgrade plan", {
          position: "top-right",
          autoClose: 3000
        });
      }
    } catch (error: any) {
      console.error("❌ Error downgrading plan:", error);

      let errorMessage = "Failed to downgrade plan";
      if (error.response && error.response.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.error ||
          errorMessage;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleCancelDowngrade = async () => {
    if (!ready || !userToken) {
      toast.error("Authentication required", {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      const response = await axios.post(
        AppConstants.SUBSCRIPTION_CANCEL_DOWNGRADE_API,
        {
          customerId: getUserIdFromToken() // Get customerId from token
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        // Refresh license data to get updated downgrade status
        await fetchAutoRenewalStatus();

        // Show success message
        const message =
          response.data.message || "Downgrade cancelled successfully";
        toast.success(message, {
          position: "top-right",
          autoClose: 3000
        });
      } else {
        toast.error(response.data.message || "Failed to cancel downgrade", {
          position: "top-right",
          autoClose: 3000
        });
      }
    } catch (error: any) {
      console.error("❌ Error cancelling downgrade:", error);

      let errorMessage = "Failed to cancel downgrade";
      if (error.response && error.response.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.error ||
          errorMessage;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleCancelConfirm = async () => {
    if (!ready || !userToken) {
      toast.error("Authentication required", {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      const response = await axios.post(
        AppConstants.SUBSCRIPTION_CANCEL_API,
        {}, // Empty body if no data needed
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        // Close the modal first
        setShowCancelModal(false);

        // Refresh the license data to get updated cancelAt information
        await fetchAutoRenewalStatus();

        // Show success message with dynamic response
        const message =
          response.data.message || "Subscription cancelled successfully";
        toast.success(message, {
          position: "top-right",
          autoClose: 5000
        });

        // Call parent callback if provided
        if (onCancelSubscription) {
          onCancelSubscription();
        }
      } else {
        toast.error(response.data.message || "Failed to cancel subscription", {
          position: "top-right",
          autoClose: 3000
        });
      }
    } catch (error: any) {
      console.error("❌ Error canceling subscription:", error);

      let errorMessage = "Failed to cancel subscription";
      if (error.response && error.response.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.error ||
          errorMessage;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  return (
    <div className="header-bar">
      <Container>
        {/* Desktop Layout */}
        <Row className="align-items-center d-none d-md-flex">
          {/* Left side - Logo */}
          <Col xs={3} className="d-flex justify-content-start">
            <img
              src={Logo}
              alt="logo"
              className="header-logo"
              style={{ cursor: "pointer" }}
              onClick={handleSubscriptionClick}
            />
          </Col>

          {/* Center - Page Title */}
          <Col xs={6} className="text-center">
            <h4 className="text-white mb-0">{title}</h4>
          </Col>

          {/* Right side - Menu Icon */}
          <Col xs={3} className="d-flex justify-content-end align-items-center">
            {showMenu && (
              <div className="d-flex align-items-center gap-3">
                {/* Three Dots for upgrade-plan and subscription */}
                {(currentPage === "upgrade-plan" ||
                  currentPage === "subscription") && (
                  <BsThreeDotsVertical
                    size={20}
                    color="white"
                    style={{ cursor: "pointer" }}
                    onClick={handleThreeDotsClick}
                  />
                )}

                {/* Menu Dropdown */}
                <Dropdown align="end">
                  <Dropdown.Toggle
                    as="button"
                    id="dropdown-menu-desktop"
                    className="btn p-2 border-0 bg-transparent dropdown-toggle-no-caret"
                    style={{ cursor: "pointer" }}
                  >
                    <img src={IconMenu} alt="menu" width={24} height={24} />
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={handleSubscriptionClick}
                      className={
                        currentPage === "subscription" ? "fw-bold" : ""
                      }
                    >
                      Subscription
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={handlePaymentMethodsClick}
                      className={
                        currentPage === "payment-methods" ? "fw-bold" : ""
                      }
                    >
                      Payment Methods
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={handleLogout}
                      className="text-danger"
                    >
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
          </Col>
        </Row>

        {/* Mobile Layout */}
        <Row className="align-items-center d-flex d-md-none">
          {/* Mobile Menu (Left) */}
          <Col xs="auto">
            {showMenu && (
              <Dropdown align="start">
                <Dropdown.Toggle
                  as="button"
                  id="dropdown-menu-mobile"
                  className="btn p-2 border-0 bg-transparent dropdown-toggle-no-caret"
                  style={{ cursor: "pointer" }}
                >
                  <img src={IconMenu} alt="menu" width={24} height={24} />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={handleSubscriptionClick}
                    className={currentPage === "subscription" ? "fw-bold" : ""}
                  >
                    Subscription
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={handlePaymentMethodsClick}
                    className={
                      currentPage === "payment-methods" ? "fw-bold" : ""
                    }
                  >
                    Payment Methods
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout} className="text-danger">
                    Logout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </Col>

          {/* Logo (Center) */}
          <Col className="text-center">
            <img
              src={Logo}
              alt="logo"
              className="header-logo"
              style={{ cursor: "pointer" }}
              onClick={handleSubscriptionClick}
            />
          </Col>

          {/* Three Dots for upgrade-plan (Right) */}
          <Col xs="auto">
            {(currentPage === "upgrade-plan" ||
              currentPage === "subscription") && (
              <BsThreeDotsVertical
                size={20}
                color="white"
                style={{ cursor: "pointer" }}
                onClick={handleThreeDotsClick}
              />
            )}
          </Col>
        </Row>
      </Container>

      {/* Cancellation Notice - Show when subscription is cancelled */}
      {licenseData?.cancelAt &&
        (currentPage === "subscription" || currentPage === "upgrade-plan") && (
          <Container className="mt-3">
            <div
              className="p-3"
              style={{
                backgroundColor: "#f8d7da",
                border: "1px solid #f5c6cb",
                borderRadius: "8px",
                color: "#721c24"
              }}
            >
              <p className="mb-0 text-center" style={{ fontSize: "14px" }}>
                You request to cancel the subscription is scheduled at{" "}
                <strong>{formatDate(licenseData.cancelAt)}</strong>. If you want
                to avail the subscription features after that time period, you
                can start a new subscription, or for any help contact support
                team.
              </p>
            </div>
          </Container>
        )}

      {/* Downgrade Notice - Show when downgrade is pending */}
      {licenseData?.downgradeStatus?.isPending &&
        (currentPage === "subscription" || currentPage === "upgrade-plan") && (
          <Container className="mt-3">
            <div
              className="p-3"
              style={{
                backgroundColor: "#f8d7da",
                border: "1px solid #f5c6cb",
                borderRadius: "8px",
                color: "#721c24"
              }}
            >
              <p
                className="mb-2 text-center"
                style={{ fontSize: "14px", fontWeight: "600" }}
              >
                Your request to downgrade the current plan is scheduled for{" "}
                <strong>
                  {formatDate(licenseData.downgradeStatus.effectiveDate)}
                </strong>
                . If there are more than{" "}
                {licenseData.downgradeStatus.newMaxUsers} users, your current
                plan will automatically continue at{" "}
                {formatDate(licenseData.downgradeStatus.effectiveDate)}.
                Downgrade will only be applicable if there are less than or
                equal to {licenseData.downgradeStatus.newMaxUsers} users.
              </p>
              <p className="mb-0 text-center" style={{ fontSize: "12px" }}>
                Meanwhile, you have the option to cancel the downgrade if you
                wish to do so.
              </p>
              <div className="text-center mt-3">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleCancelDowngrade}
                  style={{
                    borderColor: "#721c24",
                    color: "#721c24",
                    fontSize: "12px",
                    padding: "6px 16px"
                  }}
                >
                  Cancel Downgrade
                </Button>
              </div>
            </div>
          </Container>
        )}

      {/* Options Modal (Three Dots Menu) */}
      <Modal
        show={showOptionsModal}
        onHide={() => setShowOptionsModal(false)}
        centered
        dialogClassName="modal-auto-height"
      >
        <Modal.Body className="p-0">
          <div className="d-flex flex-column">
            {/* Auto-Renewal Option */}
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <span style={{ fontSize: "16px", color: "#333" }}>
                Auto - Renewal
              </span>
              <Form.Check
                type="switch"
                id="auto-renewal-switch-modal"
                checked={
                  internalAutoRenew !== null
                    ? internalAutoRenew
                    : subscriptionData?.autoRenew || false
                }
                onChange={handleAutoRenewalToggle}
                disabled={isLoadingAutoRenew}
                style={{ fontSize: "18px" }}
              />
            </div>

            {/* Downgrade Plan Option */}
            <div
              className="p-3 border-bottom"
              style={{
                cursor: licenseData?.downgradeStatus?.isPending
                  ? "not-allowed"
                  : "pointer",
                opacity: licenseData?.downgradeStatus?.isPending ? 0.6 : 1
              }}
              onClick={
                licenseData?.downgradeStatus?.isPending
                  ? undefined
                  : handleDowngradePlan
              }
            >
              <div>
                <span style={{ fontSize: "16px", color: "#333" }}>
                  Downgrade Plan
                </span>
                {licenseData?.downgradeStatus?.isPending && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "2px"
                    }}
                  >
                    Already scheduled for{" "}
                    {formatDate(licenseData.downgradeStatus.effectiveDate)}
                  </div>
                )}
              </div>
            </div>

            {/* Cancel Subscription Option */}
            <div
              className="p-3"
              style={{
                cursor: licenseData?.cancelAt ? "not-allowed" : "pointer",
                opacity: licenseData?.cancelAt ? 0.6 : 1
              }}
              onClick={
                licenseData?.cancelAt ? undefined : handleCancelSubscription
              }
            >
              <div>
                <span style={{ fontSize: "16px", color: "#dc3545" }}>
                  Cancel Subscription
                </span>
                {licenseData?.cancelAt && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "2px"
                    }}
                  >
                    Already scheduled for {formatDate(licenseData.cancelAt)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Downgrade Plan Modal */}
      <Modal
        show={showDowngradeModal}
        onHide={() => setShowDowngradeModal(false)}
        centered
        dialogClassName="modal-auto-height"
      >
        <Modal.Header className="border-0 pb-0">
          <Modal.Title
            className="w-100 text-center"
            style={{ fontSize: "18px", fontWeight: "600" }}
          >
            Downgrade Plan?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p
            className="text-center text-muted mb-4"
            style={{ fontSize: "14px" }}
          >
            You can manage up to{" "}
            <strong>{licenseData?.maxUsers || 9} users</strong> in your current
            plan.
            <br />
            How many users would you like to continue with, including yourself?
          </p>

          <div className="mb-3">
            <label
              className="form-label text-muted"
              style={{ fontSize: "14px" }}
            >
              Users <span className="text-danger">*</span>
            </label>
            <Form.Control
              type="text"
              value={downgradeUsers}
              onChange={handleDowngradeUsersChange}
              className="text-center"
              style={{ fontSize: "16px", padding: "12px" }}
              placeholder="Enter number of users"
              required
              isInvalid={!!downgradeValidationError}
            />
            {downgradeValidationError && (
              <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                {downgradeValidationError}
              </div>
            )}
            <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
              Maximum allowed: {(licenseData?.maxUsers || 9) - 1} users (must be
              less than your current plan limit)
            </div>
          </div>

          {/* Informational text - only show when user has entered a valid number */}
          {downgradeUsers && !downgradeValidationError && (
            <div
              className="text-center mb-4"
              style={{ fontSize: "14px", color: "#666" }}
            >
              You can have up to{" "}
              <strong>{licenseData?.maxUsers || 9} users</strong> until{" "}
              <strong>
                {formatDate(licenseData?.currentPeriodEnd) || "09/27/2025"}
              </strong>
              . To downgrade your plan after that, make sure your account has{" "}
              {downgradeUsers} or fewer users, including you. If not, your
              current plan will stay the same.
            </div>
          )}

          <div className="d-grid gap-2">
            <Button
              variant="success"
              size="lg"
              onClick={handleDowngradeSubmit}
              style={{
                backgroundColor: "#28a745",
                border: "none",
                borderRadius: "8px",
                padding: "12px",
                fontWeight: "600",
                color: "white"
              }}
            >
              Downgrade Plan
            </Button>
            <Button
              variant="link"
              onClick={() => setShowDowngradeModal(false)}
              className="text-success text-decoration-none"
              style={{ fontSize: "16px" }}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Cancel Subscription Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
        dialogClassName="modal-auto-height"
      >
        <Modal.Header className="border-0 pb-0">
          <Modal.Title
            className="w-100 text-center"
            style={{ fontSize: "18px", fontWeight: "600" }}
          >
            Cancel Subscription
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p
            className="text-center text-muted mb-4"
            style={{ fontSize: "14px" }}
          >
            Your subscription will remain active until{" "}
            <strong>
              {formatDate(licenseData?.currentPeriodEnd) || "09/27/2025"}
            </strong>
            . Would you like to cancel?
          </p>

          <div className="d-grid gap-2">
            <Button
              variant="success"
              size="lg"
              onClick={handleCancelConfirm}
              style={{
                backgroundColor: "#28a745",
                border: "none",
                borderRadius: "8px",
                padding: "12px",
                fontWeight: "600",
                color: "white"
              }}
            >
              Cancel Subscription
            </Button>
            <Button
              variant="link"
              onClick={() => setShowCancelModal(false)}
              className="text-success text-decoration-none"
              style={{ fontSize: "16px" }}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Header;
